import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableColumn, Input } from '@/components/common';
import { formatQuantity } from '@/utils/number-format';
import { CuttingFormOutput } from '@/types/cutting';

interface CuttingOrderLineTableProps {
  outputs: CuttingFormOutput[];
  onOutputsChange: (newOutputs: CuttingFormOutput[]) => void;
  actualQtyEditable?: boolean; // Control whether actual quantity field is editable
  noteEditable?: boolean; // Control whether note field is editable
}

const CuttingOrderLineTable: React.FC<CuttingOrderLineTableProps> = ({
  outputs,
  onOutputsChange,
  actualQtyEditable = false,
  noteEditable = false,
}) => {
  const { t } = useTranslation();

  // Prevent form submission on Enter key press in input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleActualQtyChange = (output: CuttingFormOutput, newActualQty: number) => {
    const updatedOutputs = outputs.map(o =>
      o.cutting_config_line_id === output.cutting_config_line_id
        ? { ...o, actual_qty: newActualQty }
        : o
    );
    onOutputsChange(updatedOutputs);
  };

  const handleNoteChange = (output: CuttingFormOutput, newNote: string) => {
    const updatedOutputs = outputs.map(o =>
      o.cutting_config_line_id === output.cutting_config_line_id
        ? { ...o, note: newNote }
        : o
    );
    onOutputsChange(updatedOutputs);
  };

  const columns: TableColumn<CuttingFormOutput>[] = [
    {
      key: 'material_name',
      title: t('cuttingOrders.fields.materialName'),
      dataIndex: 'material_name',
      width: 200,
    },
    {
      key: 'expected_qty',
      title: t('cuttingOrders.fields.expectedQty'),
      dataIndex: 'expected_qty',
      width: 120,
      align: 'center',
      render: (value) => formatQuantity(value),
    },
    {
      key: 'actual_qty',
      title: t('cuttingOrders.fields.actualQty'),
      dataIndex: 'actual_qty',
      width: 120,
      align: 'center',
      render: (value, record) => (
        <Input
          type="number"
          value={value}
          onChange={(e) => handleActualQtyChange(record, parseInt(e.target.value, 10))}
          className={`w-20 text-center ${!actualQtyEditable ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
          disabled={!actualQtyEditable}
          onKeyDown={handleKeyDown}
        />
      ),
    },
    {
      key: 'material_unit',
      title: t('cuttingOrders.fields.unit'),
      dataIndex: 'material_unit',
      width: 80,
      align: 'center',
    },
    {
      key: 'note',
      title: t('cuttingOrders.fields.notes'),
      dataIndex: 'note',
      width: 200,
      render: (value, record) => (
        noteEditable ? (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => handleNoteChange(record, e.target.value)}
            className="w-full text-sm"
            placeholder={t('cuttingOrders.fields.notes')}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <div className="text-sm text-gray-600 whitespace-normal break-words">
            {value || '-'}
          </div>
        )
      ),
    },
  ];

  return (
    <div className="p-4 bg-gray-50">
      <Table
        columns={columns}
        data={outputs}
        rowKey="cutting_config_line_id"
        size="sm"
      />
    </div>
  );
};

export default CuttingOrderLineTable;

